// src/pages/PlayGuide.jsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { endpoints, CHROME_STORE_URL } from '../services/api';
import { detectExtension, playWithExtension, getDomain } from '../services/extension';
import { getFriendlyError } from '../components/errorUtils';
import ReactGA from 'react-ga4';
import '../styles/pages/PlayGuide.css';

// ── Chrome Store click tracker ───────────────────────────────────────────────
// Same event name as LandingPage.js (chrome_store_click) so all Chrome Store
// clicks appear under one event in GA4.
// source='play_guide_upsell' identifies this specific placement — the upsell
// banner shown to users who don't have the extension installed when they land
// on a Play Guide page.  This is high-intent: they came to run a guide but
// don't have the extension yet.  Tracking it separately from the landing page
// CTAs lets you compare conversion rates across placements.
function trackChromeStoreClick(source) {
  ReactGA.event('chrome_store_click', {
    click_source: source,
  });
}

// Dot track shared by both views; long guides get a sampled set of dots.
const Track = ({ total, current, onPick }) => {
  const n = Math.min(total, 12);
  const idxs = Array.from({ length: n }, (_, i) => (total <= 12 ? i : Math.round((i / (n - 1)) * (total - 1))));
  const pct = total > 1 && current !== null ? (current / (total - 1)) * 100 : 0;
  return (
    <div className="pg__track">
      <span className="pg__track-fill" style={{ width: `${pct}%` }} />
      {idxs.map((idx) => {
        const cls = current === null ? '' : idx < current ? 'done' : idx === current ? 'cur' : '';
        return onPick ? (
          <button key={idx} type="button" className={cls} onClick={() => onPick(idx)} aria-label={`Go to step ${idx + 1}`} />
        ) : (
          <i key={idx} className={cls} />
        );
      })}
    </div>
  );
};

export default function PlayGuide() {
  const { guideId } = useParams();
  const navigate = useNavigate();
  const { token } = useAuth();

  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasExtension, setHasExtension] = useState(null);
  const [playState, setPlayState] = useState('idle');
  const [playError, setPlayError] = useState('');
  const [staticStep, setStaticStep] = useState(0);

  const playStateRef = useRef(playState);
  playStateRef.current = playState;

  useEffect(() => {
    if (!token) {
      setError(getFriendlyError('Please log in to view this guide.', 'Play Guide'));
      setLoading(false);
      return;
    }
    fetch(endpoints.guides.play(guideId), {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => r.ok ? r.json() : r.json().then(e => Promise.reject(e)))
      .then(data => { setGuide(data); setLoading(false); })
      .catch(err => {
        setError(getFriendlyError(err?.detail || 'Could not load this guide. Make sure you have access.', 'Play Guide'));
        setLoading(false);
      });
  }, [guideId, token]);

  useEffect(() => {
    if (!guide) return;
    detectExtension().then(setHasExtension);
  }, [guide]);

  useEffect(() => {
    const onStatus = (e) => {
      const { status, error: err } = e.detail || {};
      if (status === 'navigating') setPlayState('launching');
      if (status === 'waiting_login') setPlayState('waiting_login');
      if (status === 'started') setPlayState('playing');
      if (status === 'error') {
        setPlayState('error');
        setPlayError(err || 'Something went wrong. Please try again.');
      }
    };
    window.addEventListener('michii:play_status', onStatus);
    return () => window.removeEventListener('michii:play_status', onStatus);
  }, []);

  const launchWithExtension = useCallback(() => {
    if (!guide) return;
    setPlayState('launching');
    setPlayError('');
    playWithExtension(guide);
  }, [guide]);

  const retry = () => {
    setPlayState('idle');
    setPlayError('');
    launchWithExtension();
  };

  if (loading) return (
    <div className="pg">
      <div className="pg__loading">
        <span className="pg__spinner pg__spinner--lg" />
        <p className="m-eyebrow">Loading guide…</p>
      </div>
    </div>
  );

  if (error) return (
    <div className="pg">
      <div className="pg__panel">
        <div className="pg__head"><span>Guide playback</span><span>Error</span></div>
        <h2 className="pg__title">Unable to load guide</h2>
        <p className="pg__error">{error}</p>
        {!token && (
          <div className="pg__ctrls pg__ctrls--one">
            <button className="pg__btn pg__btn--primary" onClick={() => navigate('/login')}>
              Log in to continue
            </button>
          </div>
        )}
      </div>
    </div>
  );

  const isChecking = hasExtension === null;

  // ── Static viewer (no extension) ─────────────────────────────────────────
  if (hasExtension === false) {
    const step = guide.steps[staticStep];
    const total = guide.steps.length;
    const last = staticStep === total - 1;

    return (
      <div className="pg pg--wide">
        <div className="pg__upsell m-sheet">
          <div>
            <p className="pg__upsell-title">Let Michii point at each button for you</p>
            <p className="pg__upsell-sub">
              With the free extension, this guide runs on the real page and highlights exactly what to click.
            </p>
          </div>
          <a
            href={CHROME_STORE_URL}
            target="_blank"
            rel="noreferrer"
            className="lp-btn sm"
            onClick={() => trackChromeStoreClick('play_guide_upsell')}
          >
            Add to Chrome, free
          </a>
        </div>

        <div className="pg__manual">
          <div className="pg__sheet m-sheet">
            <div className="pg__sheet-head">
              <span className="m-badge m-badge--private">Manual view</span>
              <span className="m-eyebrow m-mono">
                {total} steps{guide.start_url ? ` · ${getDomain(guide.start_url)}` : ''}
              </span>
            </div>
            <h1 className="pg__guide-title">{guide.name}</h1>
            {guide.description && <p className="pg__guide-desc">{guide.description}</p>}
            {step?.screenshot_path && (
              <div className="pg__shot">
                <img
                  src={`${step.screenshot_path}?token=${encodeURIComponent(token)}`} //${API_BASE_URL}
                  alt={`Step ${staticStep + 1}`}
                />
              </div>
            )}
          </div>

          <div className="pg__panel pg__panel--manual" role="region" aria-label="Guide playback">
            <div className="pg__head"><span>Guide playback</span><span>{Math.round(((staticStep + 1) / total) * 100)}%</span></div>
            <Track total={total} current={staticStep} onPick={setStaticStep} />
            <p className="pg__step">Step {staticStep + 1} of {total}</p>
            <p className="pg__txt" aria-live="polite">{step?.instruction}</p>
            <div className="pg__ctrls">
              <button
                className="pg__btn"
                disabled={staticStep === 0}
                onClick={() => setStaticStep(s => s - 1)}
              >
                Previous
              </button>
              {!last ? (
                <button className="pg__btn pg__btn--primary" onClick={() => setStaticStep(s => s + 1)}>
                  Next step
                </button>
              ) : (
                <button className="pg__btn pg__btn--done" disabled>
                  ✓ Done
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Extension launcher ────────────────────────────────────────────────────
  return (
    <div className="pg">
      <div className="pg__panel" role="region" aria-label="Guide playback">
        <div className="pg__head">
          <span>Guide playback</span>
          <span>{guide.start_url ? getDomain(guide.start_url) : 'No start page'}</span>
        </div>
        <Track total={Math.max(guide.step_count || 0, 1)} current={playState === 'playing' ? 0 : null} />
        <p className="pg__step">{guide.step_count} {guide.step_count === 1 ? 'step' : 'steps'}</p>
        <h1 className="pg__title">{guide.name}</h1>
        {guide.description && <p className="pg__desc">{guide.description}</p>}

        {playState === 'idle' && (
          <>
            {isChecking ? (
              <p className="pg__note"><span className="pg__spinner" />Checking for extension…</p>
            ) : (
              <>
                <p className="pg__note"><span className="pg__dot" />Michii extension detected, ready to play</p>
                {!guide.start_url && (
                  <p className="pg__warn">
                    This guide has no recorded starting URL. Go to the right page before playing.
                  </p>
                )}
                <div className="pg__ctrls pg__ctrls--one">
                  <button className="pg__btn pg__btn--primary pg__btn--big" onClick={launchWithExtension}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                    Play guide
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {playState === 'launching' && (
          <div className="pg__status">
            <p className="pg__status-title"><span className="pg__spinner" />Opening the guide…</p>
            <p className="pg__status-sub">A new tab will open and take you to the starting page.</p>
          </div>
        )}

        {playState === 'waiting_login' && (
          <div className="pg__status pg__status--warn">
            <p className="pg__status-title">Waiting for you to log in</p>
            <p className="pg__status-sub">
              <strong>{getDomain(guide.start_url || '')}</strong> redirected you to its login page.
              Sign in there and the guide will resume automatically.
            </p>
            <div className="pg__ctrls pg__ctrls--one">
              <button className="pg__btn" onClick={() => { setPlayState('idle'); setPlayError(''); }}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {playState === 'playing' && (
          <div className="pg__status pg__status--ok">
            <p className="pg__status-title">Guide is running</p>
            <p className="pg__status-sub">Follow the highlighted steps in your browser tab.</p>
            <div className="pg__ctrls pg__ctrls--one">
              <button className="pg__btn" onClick={() => setPlayState('idle')}>
                Launch again
              </button>
            </div>
          </div>
        )}

        {playState === 'error' && (
          <div className="pg__status pg__status--error">
            <p className="pg__status-title">Something went wrong</p>
            <p className="pg__status-sub">{playError}</p>
            <div className="pg__ctrls">
              <button className="pg__btn" onClick={() => { setPlayState('idle'); setPlayError(''); }}>Cancel</button>
              <button className="pg__btn pg__btn--primary" onClick={retry}>Try again</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
